import React, { memo, useState } from 'react';
import { Button, Divider, ListItemText, Menu, MenuItem } from '@mui/material';
import { PrintOutlined } from '@mui/icons-material';
import BusyIcon from '../../common/BusyIcon';
import { timetableService, PrintView } from '../../../services/timetableService';
import { useTimetablePdf } from '../useTimetablePdf';

interface PrintMenuProps {
  sessionId: string;
  status: 'draft' | 'published';
  /** The class, teacher or room on screen, to offer printing just that one. */
  current?: { view: PrintView; id: string; label: string } | null;
}

/** PDFs of the timetable: one page per class, teacher or room. */
const PrintMenu = memo<PrintMenuProps>(({ sessionId, status, current }) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const { download, busy } = useTimetablePdf();

  const print = (view: PrintView, ids?: string[]) => {
    setAnchor(null);
    download(() => timetableService.getPrint(sessionId, status, view, ids), `Timetables_${view === 'class' ? 'Classes' : view === 'teacher' ? 'Teachers' : 'Rooms'}`);
  };

  return (
    <>
      <Button
        variant="outlined"
        color="inherit"
        startIcon={<BusyIcon busy={busy}><PrintOutlined /></BusyIcon>}
        onClick={(e) => setAnchor(e.currentTarget)}
        disabled={busy}
        sx={{ textTransform: 'none', borderColor: 'divider' }}
      >
        {busy ? 'Preparing…' : 'Print'}
      </Button>
      <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)} slotProps={{ paper: { sx: { minWidth: 220, borderRadius: '12px' } } }}>
        {current && (
          <MenuItem onClick={() => print(current.view, [current.id])}>
            <ListItemText primary={current.label} secondary="Just this one" />
          </MenuItem>
        )}
        {current && <Divider />}
        <MenuItem onClick={() => print('class')}><ListItemText primary="Every class" secondary="A page per class" /></MenuItem>
        <MenuItem onClick={() => print('teacher')}><ListItemText primary="Every teacher" secondary="A page per teacher" /></MenuItem>
        <MenuItem onClick={() => print('room')}><ListItemText primary="Special rooms" secondary="Labs and other rooms" /></MenuItem>
      </Menu>
    </>
  );
});

PrintMenu.displayName = 'PrintMenu';

export default PrintMenu;
